import {useCallback, useMemo, type CSSProperties, type ChangeEvent} from 'react'
import {Box, TextField, Select, MenuItem, IconButton, Typography, type SelectChangeEvent} from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import DeleteIcon from '@mui/icons-material/Delete'
import OffsetPad from '../sharedWidgets/OffsetPad'
import SliderRow from '../sharedWidgets/SliderRow'
import AnglePicker from '../sharedWidgets/AnglePicker'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../common/store'
import {removeText, updateText} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {ScreenText, TextAlign} from '../../../common/types/textTypes'
import {FONT_OPTIONS} from '../../../common/types/textTypes'

// Alignment toggle options — exclusive selection, one button per mode
const ALIGN_OPTIONS: {value: TextAlign; labelKey: string; Icon: typeof FormatAlignLeftIcon}[] = [
  {value: 'left', labelKey: 'text2d.alignLeft', Icon: FormatAlignLeftIcon},
  {value: 'center', labelKey: 'text2d.alignCenter', Icon: FormatAlignCenterIcon},
  {value: 'right', labelKey: 'text2d.alignRight', Icon: FormatAlignRightIcon},
  {value: 'justify', labelKey: 'text2d.alignJustify', Icon: FormatAlignJustifyIcon},
]

// ── Text2DItem — single 2D text item with controls ────────────────

interface Text2DItemProps {
  text: ScreenText
}

const Text2DItem = ({text}: Text2DItemProps) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const handleFieldChange = useCallback(
    (field: keyof ScreenText, value: string | number | boolean) => {
      dispatch(updateText({id: text.id, changes: {[field]: value}}))
    },
    [dispatch, text.id],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFieldChange('text', e.target.value)
    },
    [handleFieldChange],
  )

  const handleFontChange = useCallback(
    (e: SelectChangeEvent) => {
      handleFieldChange('fontFamily', e.target.value)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleSizeChange = useCallback(
    (value: number) => {
      handleFieldChange('fontSize', value)
    },
    [handleFieldChange],
  )

  const handleToggleBold = useCallback(() => {
    handleFieldChange('bold', !text.bold)
    dispatch(sealUndo())
  }, [handleFieldChange, text.bold, dispatch])

  const handleToggleItalic = useCallback(() => {
    handleFieldChange('italic', !text.italic)
    dispatch(sealUndo())
  }, [handleFieldChange, text.italic, dispatch])

  const handleAlign = useCallback(
    (value: TextAlign) => {
      handleFieldChange('align', value)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFieldChange('color', e.target.value)
    },
    [handleFieldChange],
  )

  const handlePositionX = useCallback(
    (value: number) => {
      handleFieldChange('x', value / 100)
    },
    [handleFieldChange],
  )

  const handlePositionY = useCallback(
    (value: number) => {
      handleFieldChange('y', value / 100)
    },
    [handleFieldChange],
  )

  const handleRotationChange = useCallback(
    (angle: number) => {
      handleFieldChange('rotation', angle)
    },
    [handleFieldChange],
  )

  const handleRemove = useCallback(() => {
    dispatch(removeText(text.id))
  }, [dispatch, text.id])

  return (
    <Box sx={sx.root}>
      {/* Header row: text input + delete */}
      <Box sx={sx.headerRow}>
        <TextField
          value={text.text}
          onChange={handleTextChange}
          onBlur={handleSealUndo}
          size="small"
          variant="outlined"
          multiline
          minRows={1}
          maxRows={6}
          sx={sx.textInput}
          placeholder={t('text2d.placeholder')}
        />
        <IconButton onClick={handleRemove} size="small" sx={sx.deleteBtn} title={t('text2d.remove')}>
          <DeleteIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>

      {/* Font select */}
      <Select
        value={text.fontFamily}
        onChange={handleFontChange}
        size="small"
        fullWidth
        sx={{...sx.select, fontFamily: text.fontFamily}}
      >
        {FONT_OPTIONS.map((font) => (
          <MenuItem key={font} value={font} sx={{fontFamily: font, fontSize: 13}}>
            {font}
          </MenuItem>
        ))}
      </Select>

      {/* Size slider */}
      <SliderRow
        label={t('text2d.fontSize')}
        value={text.fontSize}
        min={12}
        max={500}
        step={1}
        unit=""
        onChange={handleSizeChange}
        onChangeCommitted={handleSealUndo}
      />

      {/* Bold / Italic toggles + Color */}
      <Box sx={sx.controlsRow}>
        <IconButton
          onClick={handleToggleBold}
          size="small"
          sx={{...sx.toggleBtn, ...(text.bold ? sx.toggleActive : {})}}
          title={t('text2d.bold')}
        >
          <FormatBoldIcon sx={{fontSize: 16}} />
        </IconButton>
        <IconButton
          onClick={handleToggleItalic}
          size="small"
          sx={{...sx.toggleBtn, ...(text.italic ? sx.toggleActive : {})}}
          title={t('text2d.italic')}
        >
          <FormatItalicIcon sx={{fontSize: 16}} />
        </IconButton>
        <Box sx={sx.colorGroup}>
          <Typography sx={sx.label}>{t('text2d.color')}</Typography>
          <input
            type="color"
            value={text.color}
            onChange={handleColorChange}
            onBlur={handleSealUndo}
            style={sx.colorInput}
          />
        </Box>
      </Box>

      {/* Alignment toggles */}
      <Box sx={sx.alignRow}>
        {ALIGN_OPTIONS.map(({value, labelKey, Icon}) => (
          <IconButton
            key={value}
            onClick={() => handleAlign(value)}
            size="small"
            sx={{...sx.toggleBtn, ...((text.align ?? 'center') === value ? sx.toggleActive : {})}}
            title={t(labelKey)}
          >
            <Icon sx={{fontSize: 16}} />
          </IconButton>
        ))}
      </Box>

      {/* Position pad */}
      <Box sx={sx.positionSection}>
        <Typography sx={sx.label}>{t('text2d.position')}</Typography>
        <OffsetPad
          valueX={Math.round(text.x * 100)}
          valueY={Math.round(text.y * 100)}
          min={0}
          max={100}
          onChangeX={handlePositionX}
          onChangeY={handlePositionY}
          onDragEnd={handleSealUndo}
        />
      </Box>

      {/* Rotation */}
      <Box sx={sx.rotationSection}>
        <Typography sx={sx.label}>{t('text2d.rotation')}</Typography>
        <Box sx={sx.rotationRow}>
          <AnglePicker value={text.rotation} onChange={handleRotationChange} onDragEnd={handleSealUndo} />
          <Typography sx={sx.rotationValue}>{text.rotation}°</Typography>
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    padding: '8px',
    marginBottom: '8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${tokens.accordion.border}`,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '8px',
  },
  textInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: 13,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
  deleteBtn: {
    color: tokens.text.muted,
    '&:hover': {color: '#ef5350'},
  },
  select: {
    fontSize: 13,
    color: tokens.text.primary,
    marginBottom: '8px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
  label: {
    fontSize: 11,
    color: tokens.text.secondary,
    whiteSpace: 'nowrap' as const,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  alignRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  toggleBtn: {
    color: tokens.text.muted,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: '4px',
    width: 28,
    height: 28,
  },
  toggleActive: {
    color: tokens.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: tokens.text.secondary,
  },
  colorGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
  },
  positionSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
    marginTop: '8px',
  },
  rotationSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginTop: '8px',
  },
  rotationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  rotationValue: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
  },
  colorInput: {
    width: 24,
    height: 24,
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    padding: 0,
  } as CSSProperties,
})

export default Text2DItem
